import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ForgeButton,
  ForgeDialog,
  ForgeScaffold,
  ForgeSkeleton,
  ForgeStack,
  ForgeTextField,
  ForgeToolbar
} from '@tylertech/forge-react';
import { enrollMfaFactor, activateMfaFactor, removeMfaFactor } from '../../api/mfa';
import { MFA_FACTORS_QUERY_KEY } from '../../hooks/useMfaFactors';

export default function AuthenticatorSetupDialog({ open, onClose, onComplete }) {
  const queryClient = useQueryClient();

  const [factorId, setFactorId] = useState(null);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [setupError, setSetupError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasVerified, setHasVerified] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const resetState = () => {
    setFactorId(null);
    setQrCodeImage('');
    setTotpSecret('');
    setVerificationCode('');
    setSetupError('');
    setIsLoading(false);
    setIsVerifying(false);
    setHasVerified(false);
  };

  useEffect(() => {
    let isMounted = true;

    const startSetup = async () => {
      setIsLoading(true);
      setSetupError('');
      setFactorId(null);
      setQrCodeImage('');
      setTotpSecret('');
      setVerificationCode('');
      try {
        const data = await enrollMfaFactor({ factorType: 'totp' });
        if (!isMounted) {
          if (data?.factorId) {
            removeMfaFactor(data.factorId).catch(() => {});
          }
          return;
        }
        setFactorId(data?.factorId || null);
        if (data?.activation) {
          setQrCodeImage(data.activation.qrCode || '');
          setTotpSecret(data.activation.secret || '');
        }
      } catch (error) {
        if (isMounted) {
          const message = error?.response?.data?.message || 'Unable to start authenticator setup right now. Please try again.';
          setSetupError(message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (open) {
      startSetup();
    } else {
      resetState();
    }

    return () => {
      isMounted = false;
    };
  }, [open]);

  const cleanupPendingSetup = async (pendingFactorId, pendingHasVerified) => {
    if (!pendingFactorId || pendingHasVerified) return;
    try {
      await removeMfaFactor(pendingFactorId);
    } catch {
      // best effort cleanup
    }
  };

  const handleClose = async () => {
    if (isVerifying || isLoading || isClosing) return;
    const pendingFactorId = factorId;
    const pendingHasVerified = hasVerified;

    setIsClosing(true);
    try {
      if (typeof onClose === 'function') {
        onClose();
      }
      await cleanupPendingSetup(pendingFactorId, pendingHasVerified);
    } finally {
      setIsClosing(false);
    }
  };

  const handleVerify = async event => {
    event?.preventDefault?.();
    if (!factorId || isVerifying) return;

    const code = verificationCode?.trim();
    if (!code || code.length !== 6) {
      setSetupError('Enter the 6-digit verification code from your authenticator app.');
      return;
    }

    setSetupError('');
    setIsVerifying(true);
    try {
      await activateMfaFactor(factorId, code);
      setHasVerified(true);
      await queryClient.invalidateQueries({ queryKey: MFA_FACTORS_QUERY_KEY });
      if (typeof onComplete === 'function') {
        await onComplete();
      }
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (error) {
      const message = error?.response?.data?.message || 'Verification failed. Double-check the code and try again.';
      setSetupError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const canSubmit = verificationCode.length === 6 && !isVerifying && !isLoading;

  return (
    <ForgeDialog
      className="dialog--large"
      open={open}
      on-forge-dialog-close={handleClose}
      label="Set up Authenticator App"
      description="Link an authenticator app to your account. Scan the QR code or enter the secret key manually, then verify with a 6-digit code.">
      <ForgeScaffold>
        <ForgeToolbar slot="header" className="dialog-header">
          <h2 slot="start" className="forge-typography--subheading6">
            Set up Authenticator App
          </h2>
        </ForgeToolbar>
        <form slot="body" className="dialog-body" onSubmit={handleVerify}>
          <ForgeStack gap="16">
            {setupError ? <p className="dialog-status dialog-status--error">{setupError}</p> : null}
            <p className="forge-typography--body1">
              Use an authenticator app such as <strong>Google Authenticator</strong>, <strong>Authy</strong>, <strong>1Password</strong>, or <strong>OTP Manager</strong> to complete setup.
            </p>
            <ol className="forge-typography--body2 authenticator-steps">
              <li>Open your authenticator app on your mobile device.</li>
              <li>Tap the <strong>+</strong> icon or select "Add account."</li>
              <li>Scan the QR code below, or enter the secret key manually.</li>
              <li>Enter the 6-digit code generated by the app to verify.</li>
            </ol>
            <div className="authenticator-qr" role="img" aria-label="Authenticator QR code">
              {isLoading ? (
                <ForgeSkeleton form-field className="qrcode-skeleton" ariaLabel="Loading QR code" />
              ) : qrCodeImage ? (
                <img src={qrCodeImage} alt="" />
              ) : (
                <p className="forge-typography--body2 text-color--medium">QR code is not available right now.</p>
              )}
            </div>
            {totpSecret ? (
              <div className="authenticator-secret" role="region" aria-label="Secret key">
                <p className="forge-typography--body2 text-color--medium">Can't scan the code? Enter this key manually in your authenticator app:</p>
                <code>{totpSecret}</code>
              </div>
            ) : null}
            <ForgeTextField className="authenticator-input">
              <label slot="label">6-digit verification code</label>
              <input
                type="text"
                id="mfa-verification-code"
                name="verificationCode"
                value={verificationCode}
                aria-label="6-digit verification code from your authenticator app"
                autoComplete="one-time-code"
                autoFocus
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                onInput={event => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={isLoading || isVerifying}
                required
              />
            </ForgeTextField>
            <button type="submit" style={{ display: 'none' }} aria-hidden="true"></button>
          </ForgeStack>
        </form>
        <ForgeToolbar slot="footer" inverted>
          <ForgeStack slot="end" inline>
            <ForgeButton variant="outlined" onClick={handleClose} disabled={isVerifying || isLoading || isClosing}>
              {isClosing ? 'Closing...' : 'Cancel'}
            </ForgeButton>
            <ForgeButton type="button" variant="filled" onClick={handleVerify} disabled={!canSubmit}>
              {isVerifying ? 'Verifying...' : 'Verify'}
            </ForgeButton>
          </ForgeStack>
        </ForgeToolbar>
      </ForgeScaffold>
    </ForgeDialog>
  );
}
